import {UcoastEl} from "@/scripts/core/UcoastEl";
import {
	currentTargetRequired,
	getAttributeOrThrow,
	getSectionHTMLForResource,
	qsaRequired,
	qsOptional,
	qsRequired,
	removeTrapFocus,
	trapFocus,
} from '@/scripts/core/global'
import {ExpandableContent} from '@/scripts/global/ExpandableContent'
import {ScrollArrows} from '@/scripts/global/ScrollArrows'

type ReviewListItem = {
	id: string
	element: HTMLElement
	rating: number
	createdAt: number
}

export class JunipPrimaryContainer extends UcoastEl {
	static htmlSelector = 'junip-primary-container';
	filterOptions: NodeListOf<HTMLButtonElement>
	filterLabel: HTMLElement
	loadMoreButton: HTMLButtonElement
	reviews?: ReviewListItem[]
	reviewsContainer: HTMLElement

	constructor() {
		super();
		this.filterOptions = qsaRequired('[junip-filter-option]', this)
		this.loadMoreButton = qsRequired('[junip-load-more]', this)
		this.filterLabel = qsRequired('[data-filter-text]', this)
		this.reviewsContainer = qsRequired('[data-reviews-container]', this)
		this.filterOptions.forEach((option) => {
			option.addEventListener('click', this.handleFilterOptionClick.bind(this))
		})
		this.loadMoreButton.addEventListener('click', this.handleLoadMoreClick.bind(this))
	}
	handleFilterOptionClick(e: Event) {
		const target = currentTargetRequired(e)
		const text = target.textContent
		this.filterLabel.textContent = text
		const currentFilter = this.getFilterValue()
		const value = getAttributeOrThrow('junip-filter-option', target)
		if (currentFilter === value) {
			return
		}
		this.renderFilteredReviews(value)
	}

	getFilterValue() {
		return getAttributeOrThrow('data-filter', this)
	}

	async renderFilteredReviews(value: string) {
		this.setAttribute('data-filter', value)
		if (!this.reviews) {
			await this.loadReviews()
		}
		const currentCount = this.getCurrentReviewsLoadedCount();
		switch(value) {
			case 'newest':
				this.filterByNewest()
				break;
			case 'oldest':
				this.filterByOldest()
				break;
			case 'highest_rating':
				this.filterByHighestRating()
				break;
			case 'lowest_rating':
				this.filterByLowestRating()
				break;
			default:
			// code block
		}
		this.reviewsContainer.innerHTML = ''
		this.reviews?.forEach((review, index) => {
			if (index < currentCount) {
				this.reviewsContainer.appendChild(review.element)
			}
		})
		console.log('this.reviews.length', this?.reviews?.length)
		console.log('reviews at 1 star')
		console.log(this?.reviews?.filter((review) => review.rating === 1))
		this.updateLoadMoreVisibility(currentCount)
		this.recalculateExpandableAncestorHeight()
	}

	updateLoadMoreVisibility(loadedCount: number) {
		if (this.reviews && loadedCount >= this.reviews.length) {
			this.loadMoreButton.setAttribute('hidden', '')
		} else {
			this.loadMoreButton.removeAttribute('hidden')
		}
	}

	recalculateExpandableAncestorHeight() {
		const expandable = this.reviewsContainer.closest(ExpandableContent.htmlSelector) as ExpandableContent | null
		expandable?.recalculate()
	}

	getCurrentReviewsLoadedCount() {
		return parseInt(getAttributeOrThrow('data-reviews-loaded', this))
	}

	setCurrentReviewsLoadedCount(count: number) {
		this.setAttribute('data-reviews-loaded', count.toString())
	}

	getProductUrl() {
		return getAttributeOrThrow('data-product-url', this)
	}

	async loadReviews() {
		const html = await getSectionHTMLForResource(this.getProductUrl(), 'junip-container-complete')
		if (!html) throw new Error('No reviews found')
		const parser = new DOMParser()
		const doc = parser.parseFromString(html, 'text/html')
		const reviewDOMElements = qsaRequired('[data-review-id]', doc)
		this.reviews = Array.from(reviewDOMElements).map(this.createReviewListFromDOMElement)
	}

	handleLoadMoreClick(_: Event) {
		void this.loadMore()
	}

	filterByNewest() {
		this.reviews = this.reviews?.sort((a, b) => b.createdAt - a.createdAt)
	}

	filterByOldest() {
		this.reviews = this.reviews?.sort((a, b) => a.createdAt - b.createdAt)
	}

	filterByHighestRating() {
		this.reviews = this.reviews?.sort((a, b) => b.rating - a.rating)
	}

	filterByLowestRating() {
		this.reviews = this.reviews?.sort((a, b) => a.rating - b.rating)
	}

	async loadMore() {
		if (!this.reviews) {
			await this.loadReviews()
		}
		const countToLoad = 10;
		const currentCount = this.getCurrentReviewsLoadedCount()
		const newCount = currentCount + countToLoad
		this.setCurrentReviewsLoadedCount(newCount)

		this.reviews?.forEach((review, index) => {
			if (index >= currentCount && index <= newCount) {
				this.reviewsContainer.appendChild(review.element)
			}
		})
		this.updateLoadMoreVisibility(newCount)
		this.recalculateExpandableAncestorHeight()
	}

	createReviewListFromDOMElement(domElement: HTMLElement): ReviewListItem {
		const id = getAttributeOrThrow('data-review-id', domElement)
		const rating = parseInt(getAttributeOrThrow('data-review-rating', domElement))
		const createdAt = parseInt(getAttributeOrThrow('data-review-created-at', domElement))
		return {
			id,
			element: domElement,
			rating,
			createdAt
		}
	}
}

export class JunipContainerText extends UcoastEl {
	static htmlSelector = 'junip-container-text';

	constructor() {
		super();
	}
}

export class JunipContainerMedia extends UcoastEl {
	static htmlSelector = 'junip-container-media';
	scrollArrows?: ScrollArrows

	constructor() {
		super();
		const scrollContainer = qsOptional('[data-scroll-track]', this)
		const track = qsOptional('.junip-container-media__track', this)
		if (!scrollContainer || !track) return
		this.scrollArrows = new ScrollArrows({
			root: this,
			scrollContainer,
			track,
			prevButton: qsOptional('[data-prev]', this),
			nextButton: qsOptional('[data-next]', this),
		})
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.scrollArrows?.destroy()
	}
}

export class JunipFeaturedReviews extends UcoastEl {
	static htmlSelector = 'junip-featured-reviews';
	scrollArrows?: ScrollArrows
	elements?: {
		scrollContainer: HTMLElement
		track: HTMLElement
		prevButton?: HTMLElement
		nextButton?: HTMLElement
		modalRoot: HTMLElement
		modalBox: HTMLElement
		modalContent: HTMLElement
		closeButton: HTMLElement
	}
	lastFocusedCard?: HTMLElement
	boundOnModalKeyUp: (event: KeyboardEvent) => void
	boundOnTrackClick: (event: Event) => void
	boundOnTrackKeydown: (event: KeyboardEvent) => void

	constructor() {
		super();
		this.boundOnModalKeyUp = this.onModalKeyUp.bind(this)
		this.boundOnTrackClick = this.onTrackClick.bind(this)
		this.boundOnTrackKeydown = this.onTrackKeydown.bind(this)

		this.elements = this.getElements()
		if (!this.elements) return

		this.scrollArrows = new ScrollArrows({
			root: this,
			scrollContainer: this.elements.scrollContainer,
			track: this.elements.track,
			prevButton: this.elements.prevButton,
			nextButton: this.elements.nextButton,
		})

		this.addEventListener('click', this.boundOnTrackClick)
		this.addEventListener('keydown', this.boundOnTrackKeydown)
		this.elements.closeButton.addEventListener('click', () => this.closeModal())
		this.elements.modalRoot.addEventListener('click', (event) => {
			if (event.target === this.elements?.modalRoot) this.closeModal()
		})
	}

	getElements() {
		const scrollContainer = qsOptional('[data-scroll-track]', this)
		const track = qsOptional('.junip-featured-reviews__track', this)
		const modalRoot = qsOptional('[data-review-modal]', this)
		const modalBox = qsOptional('[data-modal-content]', this)
		const modalContent = qsOptional('[data-modal-review-content]', this)
		const closeButton = qsOptional('[data-modal-close]', this)
		if (!scrollContainer || !track || !modalRoot || !modalBox || !modalContent || !closeButton) {
			return undefined
		}
		return {
			scrollContainer,
			track,
			prevButton: qsOptional('[data-prev]', this),
			nextButton: qsOptional('[data-next]', this),
			modalRoot,
			modalBox,
			modalContent,
			closeButton,
		}
	}

	onTrackClick(event: Event) {
		const target = event.target as HTMLElement | null
		const card = target?.closest<HTMLElement>('[data-review-card]')
		if (!card || !this.contains(card)) return
		this.openModal(card)
	}

	onTrackKeydown(event: KeyboardEvent) {
		if (event.code !== 'Enter' && event.code !== 'Space') return
		const target = event.target as HTMLElement | null
		const card = target?.closest<HTMLElement>('[data-review-card]')
		if (!card || !this.contains(card)) return
		event.preventDefault()
		this.openModal(card)
	}

	openModal(card: HTMLElement) {
		if (!this.elements) return
		this.lastFocusedCard = card
		this.elements.modalContent.innerHTML = card.innerHTML
		this.elements.modalRoot.removeAttribute('hidden')
		document.body.classList.add('overflow-hidden')
		document.addEventListener('keyup', this.boundOnModalKeyUp)
		trapFocus(this.elements.modalBox, this.elements.closeButton)
	}

	closeModal() {
		if (!this.elements) return
		this.elements.modalRoot.setAttribute('hidden', '')
		document.body.classList.remove('overflow-hidden')
		document.removeEventListener('keyup', this.boundOnModalKeyUp)
		removeTrapFocus(this.lastFocusedCard)
	}

	onModalKeyUp(event: KeyboardEvent) {
		if (event.code?.toUpperCase() === 'ESCAPE') this.closeModal()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.scrollArrows?.destroy()
	}
}

export class JunipReviewForm extends UcoastEl {
	static htmlSelector = 'junip-review-form';

	constructor() {
		super();
	}
}


export class JunipItemImage extends UcoastEl {
	static htmlSelector = 'junip-item-image'

	constructor() {
		super()

		console.log('i have mounted?????')
	}
}